/*
 * Predictive Search — two-endpoint variant, with chained product paging
 * and hydrated cache.
 *
 * Two fetches fire per keystroke:
 *
 *   1. /search/suggest?q=…&section_id=predictive-search
 *        → sidebar (Suggestions, Category, Pages & Articles). Capped
 *          at 10 per resource type by the Predictive Search API, which
 *          is fine for the sidebar. Populates [data-search-sidebar].
 *
 *   2. /search?q=…&type=product&section_id=predictive-search-products
 *        → page 1 of products (max 50 — Shopify's {% paginate %} ceiling).
 *          Populates [data-search-products].
 *
 * After (2) lands, if the rendered grid reports more than 1 page
 * (data-total-pages on [data-search-products-grid]), parallel fetches
 * for pages 2..N fire and each page's cards are appended to the same
 * grid. That's how the dropdown shows 100+ products despite the 50 cap.
 *
 * CACHE BEHAVIOUR (important — this is where a subtle bug used to live):
 *   • The cache key is the normalised search term.
 *   • On initial fetch we store the rendered page 1 payload AND mark
 *     it as not-yet-hydrated. The follow-up fetches then append pages
 *     2..N live to the DOM, and once the last one finishes, we snapshot
 *     the full productsSlot.innerHTML back into the cache and flip the
 *     hydrated flag.
 *   • On a cache hit, if hydrated === true, we render the full grid
 *     immediately. If it's false (typed this query before but moved away
 *     before all pages landed), we render page 1 from cache AND re-fire
 *     the follow-up fetches.
 *   • Without this, the original code had: first "bed" search triggers
 *     full chain, cache stores page-1-only, user deletes to "be", user
 *     types back to "bed" → cache hit renders page 1 only → stuck at
 *     ~50 products despite results_count saying 900+. Only a hard
 *     refresh (wiping in-memory cache) would recover.
 *
 * ABORT BEHAVIOUR:
 *   • Input changes (not just form reset) now abort the previous query's
 *     pending fetches, so follow-up page requests from stale queries
 *     don't linger or trickle into the wrong grid.
 */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.sidebarSlot  = this.querySelector('[data-search-sidebar]');
    this.productsSlot = this.querySelector('[data-search-products]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  /* Cancel any in-flight fetches and start fresh. Called whenever the
   * query changes so stale page-N responses can't land in the wrong
   * grid or trigger wasted work. */
  resetAbortController() {
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Query changed — cancel any pending follow-up page fetches from
    // the previous term before firing new ones.
    if (this.searchTerm !== newSearchTerm) {
      this.resetAbortController();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.resetAbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':   this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter':     this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  /* Build the products endpoint URL for a given page. */
  buildProductsUrl(searchTerm, page) {
    let url =
      '/search'
      + `?q=${encodeURIComponent(searchTerm)}`
      + '&type=product'
      + '&options[prefix]=last'
      + '&section_id=predictive-search-products';
    if (page && page > 1) url += `&page=${page}`;
    return url;
  }

  /* Normalise a search term into the cache key. */
  cacheKey(searchTerm) {
    return searchTerm.replace(' ', '-').toLowerCase();
  }

  getSearchResults(searchTerm) {
    const queryKey = this.cacheKey(searchTerm);
    this.setLiveRegionLoadingState();
    const signal = this.abortController.signal;

    // Cache hit path.
    if (this.cachedResults[queryKey]) {
      const cached = this.cachedResults[queryKey];
      this.renderSearchResults(cached);
      if (!cached.hydrated) {
        // Page 1 was cached but pages 2..N never got folded into the
        // cache — re-fire the follow-ups so the grid fills out.
        this.fetchRemainingProductPages(searchTerm, signal);
      }
      return;
    }

    // Cache miss — fetch sidebar + products page 1 in parallel.
    const sidebarUrl =
      (typeof routes !== 'undefined' && routes.predictive_search_url
        ? routes.predictive_search_url
        : '/search/suggest')
      + `?q=${encodeURIComponent(searchTerm)}`
      + '&resources[type]=query,collection,page,article'
      + '&resources[limit]=10'
      + '&resources[limit_scope]=each'
      + '&section_id=predictive-search';

    const productsUrl = this.buildProductsUrl(searchTerm, 1);

    Promise.all([
      fetch(sidebarUrl,  { signal }).then((r) => {
        if (!r.ok) throw new Error('sidebar ' + r.status);
        return r.text();
      }),
      fetch(productsUrl, { signal }).then((r) => {
        if (!r.ok) throw new Error('products ' + r.status);
        return r.text();
      }),
    ])
      .then(([sidebarText, productsText]) => {
        // Guard: user may have moved on to a different query while we
        // were waiting. Bail before rendering stale HTML.
        if (this.searchTerm !== searchTerm) return;

        const parser = new DOMParser();
        const sidebarHtml =
          parser.parseFromString(sidebarText, 'text/html')
            .querySelector('#shopify-section-predictive-search')?.innerHTML ?? '';
        const productsHtml =
          parser.parseFromString(productsText, 'text/html')
            .querySelector('#shopify-section-predictive-search-products')?.innerHTML ?? '';

        const payload = { sidebar: sidebarHtml, products: productsHtml, hydrated: false };

        // Store page-1-only payload in every predictive-search instance's
        // cache. We'll flip `hydrated` and overwrite `products` once all
        // follow-up pages have been appended to the DOM.
        this.allPredictiveSearchInstances.forEach((inst) => {
          inst.cachedResults[queryKey] = payload;
        });

        this.renderSearchResults(payload);
        this.fetchRemainingProductPages(searchTerm, signal);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') return; // aborted
        this.close();
        throw error;
      });
  }

  /* After page 1 lands (whether from network or cache), look at the
   * grid's data-total-pages. If >1, fetch pages 2..N in parallel and
   * append cards into the live grid. When the last page finishes and
   * the query hasn't changed, snapshot the full grid HTML back into
   * the cache and mark it hydrated. */
  fetchRemainingProductPages(searchTerm, signal) {
    const queryKey = this.cacheKey(searchTerm);
    const grid = this.productsSlot?.querySelector('[data-search-products-grid]');
    if (!grid) {
      this.markCacheHydrated(searchTerm, queryKey);
      return;
    }

    const totalPages = parseInt(grid.getAttribute('data-total-pages') || '1', 10);
    if (totalPages <= 1) {
      this.markCacheHydrated(searchTerm, queryKey);
      return;
    }

    const pagesToFetch = [];
    for (let p = 2; p <= totalPages; p++) pagesToFetch.push(p);

    const parser = new DOMParser();
    let pagesRemaining = pagesToFetch.length;

    const checkDone = () => {
      pagesRemaining--;
      if (pagesRemaining === 0) {
        this.markCacheHydrated(searchTerm, queryKey);
      }
    };

    pagesToFetch.forEach((page) => {
      fetch(this.buildProductsUrl(searchTerm, page), { signal })
        .then((r) => {
          if (!r.ok) throw new Error('products page ' + page + ' ' + r.status);
          return r.text();
        })
        .then((text) => {
          // Bail if the query has changed while this fetch was in flight.
          const currentGrid = this.productsSlot?.querySelector('[data-search-products-grid]');
          if (!currentGrid || this.searchTerm !== searchTerm) return;

          const doc = parser.parseFromString(text, 'text/html');
          const newGrid = doc.querySelector('[data-search-products-grid]');
          if (!newGrid) return;

          while (newGrid.firstChild) {
            currentGrid.appendChild(newGrid.firstChild);
          }
        })
        .catch((error) => {
          if (error?.code === 20 || error?.name === 'AbortError') return;
          // Transient failure on a single follow-up page — log and keep going.
          console.warn('[predictive-search] follow-up page failed:', error);
        })
        .finally(checkDone);
    });
  }

  /* Snapshot the productsSlot's current innerHTML (which by now holds
   * page 1 + all appended follow-up pages) back into the shared cache
   * under queryKey, and flip hydrated=true. Guarded so we never
   * overwrite a cache for a query the user has already moved on from. */
  markCacheHydrated(searchTerm, queryKey) {
    if (this.searchTerm !== searchTerm) return;
    const fullHtml = this.productsSlot?.innerHTML ?? '';
    this.allPredictiveSearchInstances.forEach((inst) => {
      const existing = inst.cachedResults[queryKey];
      if (!existing) return;
      inst.cachedResults[queryKey] = {
        ...existing,
        products: fullHtml,
        hydrated: true,
      };
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText   = this.loadingText   || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  /* Inject into the two slots independently. */
  renderSearchResults(payload) {
    if (this.sidebarSlot)  this.sidebarSlot.innerHTML  = payload.sidebar  || '';
    if (this.productsSlot) this.productsSlot.innerHTML = payload.products || '';
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const region = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(region ? region.textContent : '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight =
        this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }

    // Unified-view restore: when the search is cleared/closed, swap the
    // cached default content (Popular Searches + Popular Products) back
    // into the slots so the modal always shows something useful.
    const modal = document.getElementById('search-modal-root');
    if (modal && this.sidebarSlot && typeof modal._defaultSidebarHTML === 'string') {
      this.sidebarSlot.innerHTML = modal._defaultSidebarHTML;
    }
    if (modal && this.productsSlot && typeof modal._defaultProductsHTML === 'string') {
      this.productsSlot.innerHTML = modal._defaultProductsHTML;
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);